import type {
  CarrierAddOnService,
  CarrierAddOnServiceAccess,
  CarrierAddOnServiceGrant,
  CarrierAddOnServiceType,
  PickupServiceTimeSlot,
} from "@/api.types.v2";
import { useSession } from "@/services/auth0/session";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";

const API_URL = import.meta.env.VITE_API_URL;

/**
 * Carrier-add-on-services admin client — typed generalization of the old
 * pickup-services API. Four resources:
 *   - CarrierAddOnService: provider's service header (+ pickupDetails extension)
 *   - CarrierAddOnServiceGrant: platform allowlist (provider → label carrier)
 *   - CarrierAddOnServiceAccess: carrier enablement of a specific service
 *   - PickupServiceTimeSlot: junction linking a pickup service + service area
 *     + time slot
 *
 * Follows the pickup-rate-bands.ts pattern: axios + Bearer token, `{ success,
 * data }` envelopes, list query keys + mutation invalidation.
 */

// ---------------------------------------------------------------------------
// CarrierAddOnService
// ---------------------------------------------------------------------------

/**
 * INSURANCE-only nested block. Optional on create + update. All cent fields
 * are integers; percentageRate is a fraction (e.g. 2.5 = 2.5%). Mirrors the
 * server Zod schema in create/update-carrier-add-on-service.handler.ts.
 */
export interface InsuranceServiceInput {
  coverageMaxCents?: number | null;
  currencyCode?: string;
  flatFeeCents?: number | null;
  percentageRate?: number | null;
  minimumCents?: number | null;
  maximumCents?: number | null;
}

/**
 * PACKAGING-only nested block. Optional on create + update. Either flat-fee
 * or dimensional pricing may be set (not mutually exclusive server-side).
 * `dimensionalPricing` is a nested JSON object with both fields required when
 * present.
 */
export interface PackagingServiceInput {
  flatFeeCents?: number | null;
  dimensionalPricing?: {
    perCubicCm: number;
    minimumCents: number;
  } | null;
}

/**
 * SIGNATURE-only nested block. Optional on create + update. Just an integer-
 * cents flat fee (nullable). Mirrors the server Zod schema in create/update-
 * carrier-add-on-service.handler.ts.
 */
export interface SignatureServiceInput {
  flatFeeCents?: number | null;
}

/**
 * SPECIAL_HANDLING-only nested block. Optional on create + update. Flat-fee
 * OR percentage-with-min/max clamps plus a free-form `notes` string. Mirrors
 * the server Zod schema in create/update-carrier-add-on-service.handler.ts.
 */
export interface SpecialHandlingServiceInput {
  flatFeeCents?: number | null;
  percentageRate?: number | null;
  minimumCents?: number | null;
  maximumCents?: number | null;
  notes?: string | null;
}

export interface CreateCarrierAddOnServiceInput {
  providerCarrierId: string;
  type: CarrierAddOnServiceType;
  code: string;
  name: string;
  active?: boolean;
  dimFactorCm?: number | null;
  dimFactorInch?: number | null;
  insurance?: InsuranceServiceInput;
  packaging?: PackagingServiceInput;
  signature?: SignatureServiceInput;
  specialHandling?: SpecialHandlingServiceInput;
}

export interface UpdateCarrierAddOnServiceInput {
  name?: string;
  active?: boolean;
  dimFactorCm?: number | null;
  dimFactorInch?: number | null;
  insurance?: InsuranceServiceInput;
  packaging?: PackagingServiceInput;
  signature?: SignatureServiceInput;
  specialHandling?: SpecialHandlingServiceInput;
}

export function useListCarrierAddOnServices(params?: {
  providerCarrierId?: string;
  type?: CarrierAddOnServiceType;
  includeInactive?: boolean;
}) {
  const { token } = useSession();

  return useQuery({
    queryKey: ["carrier-add-on-services", params],
    queryFn: async () => {
      const q = new URLSearchParams();
      if (params?.providerCarrierId)
        q.append("providerCarrierId", params.providerCarrierId);
      if (params?.type) q.append("type", params.type);
      if (params?.includeInactive) q.append("includeInactive", "true");

      const { data } = await axios.get<{
        success: boolean;
        data: CarrierAddOnService[];
      }>(
        `${API_URL}/api/v2/admin/carrier-add-on-services${q.toString() ? `?${q.toString()}` : ""}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data.data;
    },
    enabled: !!token,
  });
}

export function useCreateCarrierAddOnService(options?: {
  onSuccess?: (data: CarrierAddOnService) => void;
  onError?: (error: Error) => void;
}) {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateCarrierAddOnServiceInput) => {
      const { data } = await axios.post<{
        success: boolean;
        data: CarrierAddOnService;
      }>(`${API_URL}/api/v2/admin/carrier-add-on-services`, input, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      return data.data;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({
        queryKey: ["carrier-add-on-services"],
      });
      options?.onSuccess?.(data);
    },
    onError: options?.onError,
  });
}

export function useUpdateCarrierAddOnService(options?: {
  onSuccess?: (data: CarrierAddOnService) => void;
  onError?: (error: Error) => void;
}) {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data: updateData,
    }: {
      id: string;
      data: UpdateCarrierAddOnServiceInput;
    }) => {
      const { data } = await axios.patch<{
        success: boolean;
        data: CarrierAddOnService;
      }>(`${API_URL}/api/v2/admin/carrier-add-on-services/${id}`, updateData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      return data.data;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({
        queryKey: ["carrier-add-on-services"],
      });
      options?.onSuccess?.(data);
    },
    onError: options?.onError,
  });
}

export function useDeleteCarrierAddOnService(options?: {
  onSuccess?: (data: CarrierAddOnService) => void;
  onError?: (error: Error) => void;
}) {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await axios.delete<{
        success: boolean;
        data: CarrierAddOnService;
      }>(`${API_URL}/api/v2/admin/carrier-add-on-services/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return data.data;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({
        queryKey: ["carrier-add-on-services"],
      });
      options?.onSuccess?.(data);
    },
    onError: options?.onError,
  });
}

// ---------------------------------------------------------------------------
// CarrierAddOnServiceGrant — platform allowlist
// ---------------------------------------------------------------------------

export interface CreateCarrierAddOnServiceGrantInput {
  providerCarrierId: string;
  serviceType: CarrierAddOnServiceType;
  labelCarrierId: string;
}

export function useListCarrierAddOnServiceGrants(params?: {
  providerCarrierId?: string;
  serviceType?: CarrierAddOnServiceType;
  labelCarrierId?: string;
}) {
  const { token } = useSession();

  return useQuery({
    queryKey: ["carrier-add-on-service-grants", params],
    queryFn: async () => {
      const q = new URLSearchParams();
      if (params?.providerCarrierId)
        q.append("providerCarrierId", params.providerCarrierId);
      if (params?.serviceType) q.append("serviceType", params.serviceType);
      if (params?.labelCarrierId)
        q.append("labelCarrierId", params.labelCarrierId);

      const { data } = await axios.get<{
        success: boolean;
        data: CarrierAddOnServiceGrant[];
      }>(
        `${API_URL}/api/v2/admin/carrier-add-on-service-grants${q.toString() ? `?${q.toString()}` : ""}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data.data;
    },
    enabled: !!token,
  });
}

export function useCreateCarrierAddOnServiceGrant(options?: {
  onSuccess?: (data: CarrierAddOnServiceGrant) => void;
  onError?: (error: Error) => void;
}) {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateCarrierAddOnServiceGrantInput) => {
      const { data } = await axios.post<{
        success: boolean;
        data: CarrierAddOnServiceGrant;
      }>(`${API_URL}/api/v2/admin/carrier-add-on-service-grants`, input, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      return data.data;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({
        queryKey: ["carrier-add-on-service-grants"],
      });
      options?.onSuccess?.(data);
    },
    onError: options?.onError,
  });
}

/**
 * Server hard-deletes the grant AND cascade-deletes dependent access rows in
 * the same transaction — so invalidate the access list too. Response carries
 * the cascaded-access count.
 */
export function useDeleteCarrierAddOnServiceGrant(options?: {
  onSuccess?: (data: { id: string; cascadedAccessCount: number }) => void;
  onError?: (error: Error) => void;
}) {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await axios.delete<{
        success: boolean;
        data: { id: string; cascadedAccessCount: number };
      }>(`${API_URL}/api/v2/admin/carrier-add-on-service-grants/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return data.data;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({
        queryKey: ["carrier-add-on-service-grants"],
      });
      void queryClient.invalidateQueries({
        queryKey: ["carrier-add-on-service-access"],
      });
      options?.onSuccess?.(data);
    },
    onError: options?.onError,
  });
}

// ---------------------------------------------------------------------------
// CarrierAddOnServiceAccess — carrier enablement
// ---------------------------------------------------------------------------

export interface UpsertCarrierAddOnServiceAccessInput {
  carrierAddOnServiceId: string;
  labelCarrierId: string;
  enabled: boolean;
}

export function useListCarrierAddOnServiceAccess(params?: {
  carrierAddOnServiceId?: string;
  labelCarrierId?: string;
}) {
  const { token } = useSession();

  return useQuery({
    queryKey: ["carrier-add-on-service-access", params],
    queryFn: async () => {
      const q = new URLSearchParams();
      if (params?.carrierAddOnServiceId)
        q.append("carrierAddOnServiceId", params.carrierAddOnServiceId);
      if (params?.labelCarrierId)
        q.append("labelCarrierId", params.labelCarrierId);

      const { data } = await axios.get<{
        success: boolean;
        data: CarrierAddOnServiceAccess[];
      }>(
        `${API_URL}/api/v2/admin/carrier-add-on-service-access${q.toString() ? `?${q.toString()}` : ""}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data.data;
    },
    enabled: !!token,
  });
}

export function useUpsertCarrierAddOnServiceAccess(options?: {
  onSuccess?: (data: CarrierAddOnServiceAccess) => void;
  onError?: (error: Error) => void;
}) {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: UpsertCarrierAddOnServiceAccessInput) => {
      const { data } = await axios.post<{
        success: boolean;
        data: CarrierAddOnServiceAccess;
      }>(`${API_URL}/api/v2/admin/carrier-add-on-service-access`, input, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      return data.data;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({
        queryKey: ["carrier-add-on-service-access"],
      });
      options?.onSuccess?.(data);
    },
    onError: options?.onError,
  });
}

// ---------------------------------------------------------------------------
// PickupServiceTimeSlot — junction linking a PICKUP service + service area +
// time slot. The list endpoint filters by carrierAddOnServiceId / serviceAreaId
// (NOT timeSlotId) — so the linking UI is keyed on (service, area), and the
// time slot is the dimension being added/removed within that scope.
// ---------------------------------------------------------------------------

export interface LinkPickupServiceTimeSlotInput {
  carrierAddOnServiceId: string;
  serviceAreaId: string;
  timeSlotId: string;
}

export function useListPickupServiceTimeSlots(params?: {
  carrierAddOnServiceId?: string;
  serviceAreaId?: string;
}) {
  const { token } = useSession();

  return useQuery({
    queryKey: ["pickup-service-time-slots", params],
    queryFn: async () => {
      const q = new URLSearchParams();
      if (params?.carrierAddOnServiceId)
        q.append("carrierAddOnServiceId", params.carrierAddOnServiceId);
      if (params?.serviceAreaId)
        q.append("serviceAreaId", params.serviceAreaId);

      const { data } = await axios.get<{
        success: boolean;
        data: PickupServiceTimeSlot[];
      }>(
        `${API_URL}/api/v2/admin/pickup-service-time-slots${q.toString() ? `?${q.toString()}` : ""}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data.data;
    },
    enabled: !!token,
  });
}

export function useLinkPickupServiceTimeSlot(options?: {
  onSuccess?: (data: PickupServiceTimeSlot) => void;
  onError?: (error: Error) => void;
}) {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: LinkPickupServiceTimeSlotInput) => {
      const { data } = await axios.post<{
        success: boolean;
        data: PickupServiceTimeSlot;
      }>(`${API_URL}/api/v2/admin/pickup-service-time-slots`, input, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      return data.data;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({
        queryKey: ["pickup-service-time-slots"],
      });
      options?.onSuccess?.(data);
    },
    onError: options?.onError,
  });
}

/**
 * The junction has a compound PK — no single `:id`. The three ids go as query
 * params on the DELETE.
 */
export function useUnlinkPickupServiceTimeSlot(options?: {
  onSuccess?: (data: LinkPickupServiceTimeSlotInput) => void;
  onError?: (error: Error) => void;
}) {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: LinkPickupServiceTimeSlotInput) => {
      const q = new URLSearchParams({
        carrierAddOnServiceId: input.carrierAddOnServiceId,
        serviceAreaId: input.serviceAreaId,
        timeSlotId: input.timeSlotId,
      });
      const { data } = await axios.delete<{
        success: boolean;
        data: LinkPickupServiceTimeSlotInput;
      }>(`${API_URL}/api/v2/admin/pickup-service-time-slots?${q.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return data.data;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({
        queryKey: ["pickup-service-time-slots"],
      });
      options?.onSuccess?.(data);
    },
    onError: options?.onError,
  });
}
